import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import { PendingInvoicesSkeleton } from './PendingInvoicesSkeleton';
import { PendingInvoicesView } from './PendingInvoicesView';
import type { PendingInvoice } from './pendingInvoicesMeta';
import { usePendingInvoices } from './usePendingInvoices';

type PendingInvoicesContentProps = {
    error: ErrorResponse | null;
    isLoading: boolean;
    items: PendingInvoice[];
    onImport: (invoiceId: string) => void;
};

/**
 * Self-served Incoming-Invoices widget. Owns its data hook and is
 * wrapped in an `ErrorBoundary` so a render-time failure can't break
 * the Purchases page. The throw lives in the child component because
 * React boundaries do not catch errors thrown by the component that
 * renders them — only by children.
 */
export const PendingInvoices: React.FC = () => {
    const { data, error, isLoading, onImport, onRetry } = usePendingInvoices();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('purchases.widgets.pendingInvoices.errorTitle'),
            errorMessage: t('purchases.widgets.pendingInvoices.errorMessage'),
            errorRetry: t('purchases.widgets.pendingInvoices.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="pending-invoices-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="pending-invoices-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <PendingInvoicesContent
                error={error}
                isLoading={isLoading}
                items={data}
                onImport={onImport}
            />
        </ErrorBoundary>
    );
};

const PendingInvoicesContent: React.FC<PendingInvoicesContentProps> = ({
    error,
    isLoading,
    items,
    onImport,
}) => {
    // Only throw on the *initial* error — once rows are on screen, keep
    // them visible and surface refresh failures elsewhere (mirrors the
    // Transactions widget's behaviour).
    if (error && items.length === 0) {
        throw error;
    }

    if (isLoading && items.length === 0) {
        return <PendingInvoicesSkeleton />;
    }

    return <PendingInvoicesView items={items} onImport={onImport} />;
};
